import React from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

interface ChartPoint {
  x: number;
  y: number;
}

interface ChartSeries {
  name: string;
  data: ChartPoint[];
}

interface CustomLineChartProps {
  series: ChartSeries[];
  color?: string;
  height?: number | string;
  width?: number | string;
  maxCpcValue?: number | string | null;
  threshold?: number | null
}

const computeYAtX = (data: ChartPoint[], x: number): number | null => {
  const sorted = [...data].sort((a, b) => a.x - b.x);

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];

    if (a.x === b.x) continue;

    if (x >= a.x && x <= b.x) {
      const t = (x - a.x) / (b.x - a.x);
      return a.y + t * (b.y - a.y);
    }
  }

  return null;
};

const CustomLineChart: React.FC<CustomLineChartProps> = ({
  series,
  color = "#38BDF8",
  height = 70,
  width = "100%",
  maxCpcValue,
  threshold
}) => {

  const baseSeries = series[0]?.data ?? [];
  const safeMax = Number(maxCpcValue);

  const markerY =
    !isNaN(safeMax) ? computeYAtX(baseSeries, safeMax) : null;

  const ys = baseSeries.map(p => p.y);

  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  // marge visuelle (0.1% ici)
  const padding = Math.max((maxY - minY) * 0.3, 0.05);

  const yMin = minY - padding;
  const yMax = maxY + padding;


  const options: ApexOptions = {
    chart: {
      type: "line",
      sparkline: { enabled: true },
      animations: { enabled: false },
      toolbar: { show: false },
      zoom: { enabled: false },
    },


    colors: [color],

    stroke: {
      width: 2,
      curve: "smooth",
    },

    markers: {
      size: 0,
    },

    annotations: {
      points:
        markerY !== null
          ? [
            {
              x: safeMax,
              y: markerY,
              marker: {
                size: 5,
                fillColor: "#0D1218",
                strokeColor: color,
                strokeWidth: 2,
              },
              label: {
                borderColor: "transparent",
                text: safeMax.toFixed(2),
                offsetY: markerY > 70 ? 40 : -3,
                style: {
                  background: "transparent",
                  color: "#FFFFFF",
                  fontSize: "10px",
                  fontWeight: 600,

                },
              },
            },
          ]
          : [],
      xaxis: typeof safeMax === "number" && !isNaN(safeMax)
        ? [
          {
            x: safeMax,
            borderColor: color,
            strokeDashArray: 4,
          },
        ]
        : [],
      yaxis:
        typeof threshold === "number"
          ? [
            {
              y: threshold,
              borderColor: "rgba(255,255,255,0.35)",
              strokeDashArray: 4,
              label: {
                position: "left",

                text: `${threshold}%`,
                borderColor: "transparent",
                style: {
                  background: "transparent",
                  color: "#FFFFFF",
                  fontSize: "11px",
                  fontWeight: 500,
                },
                offsetX: 25,
                offsetY: 20,
              },
            },
          ]
          : [],
    },


    tooltip: { enabled: false },

    xaxis: {
      axisBorder: {
        show: false,
      },
      labels: { show: false },
    },

    yaxis: {
      axisBorder: {
        show: false,
      },
      labels: { show: false },
      min: 0,
      max: 100,
    },

    grid: { show: false },
  };

  return (
    <Chart
      type="line"
      options={options}
      series={[
        {
          name: series[0].name,
          data: series[0].data,
        },
      ]}
      height={height}
      width={width}
    />
  );
};

export default CustomLineChart;
